import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaArrowUp,
  FaInstagram
} from 'react-icons/fa';
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  CONTACT_ADDRESS_DISPLAY,
  CONTACT_MAPS_URL,
  CONTACT_INSTAGRAM_URL
} from '../config/contact';

const FooterContainer = styled.footer`
  background: var(--secondary-color);
  color: var(--white-color);
  padding: 60px 0 20px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FooterTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--primary-color);
`;

const FooterText = styled.p`
  line-height: 1.6;
  opacity: 0.8;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterLink = styled(Link)`
  color: var(--white-color);
  text-decoration: none;
  transition: var(--transition);
  opacity: 0.8;
  
  &:hover {
    color: var(--primary-color);
    opacity: 1;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  opacity: 0.8;
  
  svg {
    color: var(--primary-color);
    flex-shrink: 0;
  }
`;

const FooterContactLink = styled.a`
  color: inherit;
  text-decoration: none;
  &:hover {
    color: var(--primary-color);
    text-decoration: underline;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
`;

const Copyright = styled.p`
  opacity: 0.8;
  font-size: 14px;
`;

const ScrollToTop = styled.button`
  background: var(--primary-color);
  color: var(--white-color);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: var(--transition);
  
  &:hover {
    background: #e67e00;
    transform: translateY(-2px);
  }
`;

const FooterInstagramLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: var(--white-color);
  font-size: 22px;
  transition: var(--transition);

  &:hover {
    background: var(--primary-color);
    color: var(--white-color);
    transform: translateY(-2px);
  }
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterSection>
            <FooterTitle>SULTANLAR BESİCİLİK</FooterTitle>
            <FooterText>
              25 yılı aşkın tecrübemizle besi sektöründe güvenilir çözümler sunuyoruz. 
              Kaliteli hayvan yetiştirme ve satışında uzman ekibimizle hizmetinizdeyiz.
            </FooterText>
          </FooterSection>
          
          <FooterSection>
            <FooterTitle>Hızlı Linkler</FooterTitle>
            <FooterLinks>
              <FooterLink to="/">Anasayfa</FooterLink>
              <FooterLink to="/hakkimizda">Hakkımızda</FooterLink>
              <FooterLink to="/hizmetler">Hizmetler</FooterLink>
              <FooterLink to="/hayvanlar">Hayvanlar</FooterLink>
              <FooterLink to="/iletisim">İletişim</FooterLink>
            </FooterLinks>
          </FooterSection>
          
          <FooterSection>
            <FooterTitle>Hizmetlerimiz</FooterTitle>
            <FooterLinks>
              <FooterLink to="/hizmetler">Büyükbaş Hayvanlar</FooterLink>
              <FooterLink to="/hizmetler">Küçükbaş Hayvanlar</FooterLink>
              <FooterLink to="/hizmetler">Kurban Hizmetleri</FooterLink>
              <FooterLink to="/hizmetler">Nakliye Hizmetleri</FooterLink>
              <FooterLink to="/hizmetler">Sağlık Garantisi</FooterLink>
            </FooterLinks>
          </FooterSection>
          
          <FooterSection>
            <FooterTitle>İletişim Bilgileri</FooterTitle>
            <ContactInfo>
              <ContactItem>
                <FaMapMarkerAlt />
                <FooterContactLink href={CONTACT_MAPS_URL} target="_blank" rel="noopener noreferrer">
                  {CONTACT_ADDRESS_DISPLAY}
                </FooterContactLink>
              </ContactItem>
              <ContactItem>
                <FaPhone />
                <FooterContactLink href={CONTACT_PHONE_TEL}>{CONTACT_PHONE_DISPLAY}</FooterContactLink>
              </ContactItem>
              <ContactItem>
                <FaEnvelope />
                <FooterContactLink href={CONTACT_MAILTO}>{CONTACT_EMAIL}</FooterContactLink>
              </ContactItem>
              <ContactItem>
                <FaInstagram />
                <FooterContactLink
                  href={CONTACT_INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </FooterContactLink>
              </ContactItem>
            </ContactInfo>
          </FooterSection>
        </FooterContent>
        
        <FooterBottom>
          <Copyright>
            © 2024 SULTANLAR BESİCİLİK. Tüm hakları saklıdır.
          </Copyright>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FooterInstagramLink
              href={CONTACT_INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram — Burdur Sultanlar Besi Çiftliği"
            >
              <FaInstagram />
            </FooterInstagramLink>
            <ScrollToTop type="button" onClick={scrollToTop} aria-label="Yukarı çık">
              <FaArrowUp />
            </ScrollToTop>
          </div>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 